import type { ReactNode } from 'react'
import { buildDashboard } from '@solum/experience'
import { formatDate, formatMoney } from '@solum/format'
import { useNavigate } from '@tanstack/react-router'
import { TrendingUp } from 'lucide-react'
import {
  useSemantics, useMeasures, useMyWork, useConsoleDashboard, useEntityIdentities, useMyProfile,
  type MeasureValue,
} from '../../data/queries'
import { QueryBoundary, queryState } from '../../data/QueryBoundary'
import {
  Button, DashboardCard, Pill, StatStrip, WorkHome,
  type StatStripTile, type WorkHomeFeed, type WorkHomeItem,
} from '@solum/ui'
import s from './Home.module.css'
import { resolveHomeDashboard } from './homeDashboard'

/**
 * THE OPERATOR HOME, on the same premium grammar as the customer one (#2406).
 *
 * ── what was wrong, and it was not a missing feature ───────────────────────
 * `WorkHome` has always accepted `appearance`, `highlights`, `status`,
 * `primaryAction` and `secondaryActions`, and passes all five straight through
 * to `PageShell`. This page passed NONE of them. So the console rendered the
 * pale default header and dropped its governed figures into the body as two
 * loose tile grids under hand-written group labels, while the portal — same
 * shell, same props — rendered the dark cap with its measures inside it. The
 * premium treatment was never portal-only; the console never asked for it.
 *
 * The correction is therefore an ADOPTION, not a second dashboard: this file
 * gets shorter, its private stylesheet nearly disappears, and no markup moves
 * from the portal into the console.
 *
 * ── what is NOT here, and why ──────────────────────────────────────────────
 * No environment/module matrix. The portal's is bound to a customer's Elevate
 * estate; the console has no governed row/column source that answers an
 * operator question, and a matrix drawn without one would be visual similarity
 * standing in for fabricated semantics.
 *
 * No "what changed" rail: there is no governed console source for it on this
 * recipe, so it is reported as an omission rather than invented.
 *
 * Nothing is counted in the browser. Every figure is `core.dashboard_measures`
 * under the caller's own RLS, and the work feed is a capped list that is never
 * reduced into a number.
 */

/** Format a measure the way the REGISTRY says, not the way this page guesses. */
function show(m: MeasureValue): string | null {
  /* `null`, not a dash: `StatStrip` models the honest absence itself and says
     WHY beneath the tile. A dash returned here would be this page inventing a
     presentation the kit already owns. */
  if (m.error) return null
  if (m.value == null) return null
  if (m.format === 'money') return formatMoney(Math.round(m.value), { gap: ' ', locale: 'en-US' })
  if (m.format === 'percent') return `${Math.round(m.value)}%`
  return String(m.value)
}

/** Why a figure is absent, in the words a reader needs. */
function absence(m: MeasureValue | undefined): string {
  if (!m) return 'not registered here'
  if (m.error) return 'could not be computed'
  return 'no data yet'
}

function greeting(name: string): string {
  const h = new Date().getHours()
  const part = h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening'
  return `Good ${part}, ${name}`
}

export function Home() {
  const sem = useSemantics('crm_opportunity')
  const measures = useMeasures()
  const dashboard = useConsoleDashboard('home')
  const work = useMyWork()
  const identities = useEntityIdentities()
  /* The SAME query the account menu already reads (`core.my_profile`), so the
     greeting costs no second identity lookup — React Query serves both callers
     from one key. Until it answers, the summary stays the sentence it was. */
  const profile = useMyProfile()
  const navigationByEntity = new Map((identities.data ?? [])
    .map((identity) => [identity.entity_type, identity.navigation_eligible === true]))
  const navigate = useNavigate()
  const go = (to: string) => (navigate as unknown as (options: { to: string }) => void)({ to })

  /* WHICH figures this deployment sees is DATA (#1188); the literal survives
     only as the outage path. `composition.state` is the seam that says which of
     the two is on screen, and it rides on the strip itself now rather than on a
     heading beside the figures it describes. */
  const composition = resolveHomeDashboard(dashboard.data, dashboard.isSuccess)
  const byKey = new Map((measures.data ?? []).map((m) => [m.key, m]))
  const tileFor = (key: string, label: string, sub?: string): StatStripTile => {
    const m = byKey.get(key)
    return { key, label: m?.label ?? label, value: m ? show(m) : null, unavailable: absence(m), sub }
  }
  const capTiles = composition.measureKeys.map((key) => tileFor(key, key))

  const gate = queryState([sem, measures])
  /* THE OVERVIEW IS A DASHBOARD CARD (#2406 cards-inputs, "Dashboards and
     Cards"): a slab with a head — the brand mark, "Pipeline", the one line that
     says where the figures come from — and the figures in its body, where a
     strip cut into a card takes the RECESS rather than lifting a second slab.
     The card is only ever as sure as its source:

       · reading    → the card's loading state: skeleton rows in the recess, in
                      the success geometry, and no figure (no `0` before the
                      measures have answered);
       · refused or failed → the governed `QueryBoundary`, INSIDE the card, so
                      a refusal keeps the card's geometry and never masquerades
                      as "nothing here";
       · ready, and the semantic layer declares no pipeline figures → no card
                      and no section, as before: a card about a pipeline this
                      deployment does not describe would take room it does not
                      use. */
  const pipelineCard = (body: { state?: 'loading'; children?: ReactNode }) => (
    <DashboardCard
      title="Pipeline"
      sub="Opportunities, from the semantic layer, under your own access"
      icon={<TrendingUp aria-hidden />}
      testId="console-home-overview"
      state={body.state}
    >
      {body.children}
    </DashboardCard>
  )
  let overview
  if (gate.kind === 'loading') {
    overview = pipelineCard({ state: 'loading' })
  } else if (gate.kind !== 'ready') {
    overview = pipelineCard({
      children: <QueryBoundary queries={[sem, measures]} label="the overview">{() => null}</QueryBoundary>,
    })
  } else {
    /* The pipeline stays where it has always come from: the semantic layer says
       which cards exist, `core.dashboard_measures` computes them. */
    const cards = sem.data ? buildDashboard(sem.data, 'crm_opportunity') : []
    overview = cards.length > 0
      ? pipelineCard({
        children: (
          <StatStrip
            label="Pipeline, from the semantic layer"
            testId="console-home-pipeline"
            loading={measures.isPending}
            /* No caption. This passed `card.aggregation`, so every tile read
               "count", "avg" or "sum" under its figure — the name of a SQL
               function, and under an absent value ("— avg") the only words on
               the tile. Seen on the live console, 2026-09-22. */
            tiles={cards.map((card) => tileFor(card.key, card.label))}
          />
        ),
      })
      : null
  }

  const workItems: WorkHomeItem[] = ((work.data ?? []) as Record<string, unknown>[])
    /* The eligibility guard is what makes a work item openable at all: a row
       whose entity this deployment does not navigate to would draw an action
       that goes nowhere. */
    .filter((row) => navigationByEntity.get(String(row.entity_type ?? '')) === true)
    .map((row) => {
      const entity = String(row.entity_type ?? '')
      const id = String(row.id ?? '')
      const dueAt = row.due_at ? formatDate(row.due_at) : null
      return {
        id: `${entity}:${id}`,
        title: String(row.title ?? 'Untitled work'),
        detail: 'Assigned to you',
        meta: dueAt ? `Due ${dueAt}` : undefined,
        status: row.status ? <Pill tone="neutral">{String(row.status)}</Pill> : undefined,
        featured: Number(row.priority) === 1,
        onOpen: () => go(`/e/${entity}/${id}`),
        openLabel: 'Open work',
      }
    })

  const attention: WorkHomeFeed = work.isPending
    ? { state: 'loading', label: 'Finding your work…' }
    : work.isError
      ? {
        state: 'error',
        message: 'Your work could not be read.',
        action: <Button size="sm" onClick={() => { void work.refetch() }}>Try again</Button>,
      }
      : {
        state: 'ready',
        items: workItems,
        /* A calm sentence, and it is the ANSWER to the heading above it rather
           than a second heading. */
        empty: <p className={s.emptyLine}>Nothing is assigned to you right now.</p>,
      }

  /* ONLY when the feed supports the statement: ready, and empty. A pill saying
     "all caught up" while the work is still being read, or while a refusal is
     on screen, is the page telling a reader something it does not know. */
  const caughtUp = !work.isPending && !work.isError && workItems.length === 0

  return (
    <WorkHome
      /* The operator context is kept exactly — Workspace / What needs you. The
         greeting rides in the SUMMARY rather than replacing the heading, so the
         page keeps its accessible name and the reader is still named. */
      eyebrow="Workspace"
      title="What needs you"
      summary={profile.data?.display_name
        ? `${greeting(String(profile.data.display_name))}. Your assigned work, across every installed module.`
        : 'Your assigned work and the next useful actions, across every installed module.'}
      status={caughtUp ? <Pill tone="good">All caught up</Pill> : undefined}
      appearance="feature"
      highlights={capTiles.length > 0
        ? (
          <StatStrip
            label="This workspace at a glance"
            testId="console-home-measures"
            loading={measures.isPending}
            tiles={capTiles}
            data-dashboard-composition={composition.state}
            data-dashboard-items={composition.state === 'final' ? composition.itemKeys.join(' ') : undefined}
          />
        )
        : undefined}
      attentionLabel="Needs your attention"
      attentionSummary="Open the work to see its governed context, rules and available actions."
      attention={attention}
      secondaryLabel={overview ? 'Overview' : undefined}
      secondary={overview}
      testId="console-work-home"
    />
  )
}
